// v3 wall, authored the way age-of-mcp writes it: local field helpers + the
// shared styleV3 part makers, instead of a flat wall of literals. A clean
// DRESSED-STONE curtain wall whose identity lives in its top line — a pale
// crenellated cap course riding every run, so from above a finished wall reads
// as a crisp light line studded with merlons over the darker stone body. The
// gate is a clear two-pillar ARCH carrying a bold red BANNER (ROOF_TRADE) above
// timber door wings that swing with `gateOpen`. Ported 1:1 from age-of-mcp's
// v3/wall.ts.
//
// Protocol quirks preserved exactly (see the classic model):
//  - `connections` is the 8-bit neighbour mask (bit 0 = E, counter-clockwise;
//    orthogonals on even bits, diagonals on odd). A diagonal arm is SKIPPED
//    when EITHER flanking orthogonal also connects (the classic WALL rule),
//    keeping straight runs and solid blocks tidy.
//  - `heights[i]` = (boundary height − my height) per connection bit: arms
//    are HALF-arms from the post centre to the shared tile-boundary midpoint,
//    tilted with euler order 'YZX' (= Ry(yaw)·Rz(pitch) — pitch in the arm's
//    own frame first, then yaw to the compass direction), so two adjacent v3
//    walls always meet flush at the same world height on a slope.
//  - `gate`/`gateOpen`: a wall converted to a gate draws the banner arch
//    instead; its orientation reads the orthogonal connection bits (an E-W
//    wall run opens a N-S passage). The arch stands TALLER than the wall.
//  - Four build stages rise like the classic (merlons appear at stage 3).
//
// This standalone model renders the default standalone post (connections = 0,
// not a gate), which is the look the flat baseline bakes; the full arm/gate
// machinery is ported verbatim and exercised when those args are supplied.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { STONE, STONE_DK, PLASTER, TIMBER, ROOF_TRADE, box } = styleV3;

const IRON = "#3e3e44";   // iron door bands

// The eight neighbour directions in connection-mask bit order. Local +X is
// east, local +Z is north — matching how the renderer computes the mask.
const DIRS8: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
];

// Rampart height (top of the cap course) per build stage.
const STAGE_H = [0.12, 0.22, 0.36, 0.5];
const THICK = 0.16;   // wall-body thickness
const CAP_H = 0.06;   // pale cap course riding every run

// The half-arm frame toward neighbour bit `i`: horizontal run to the shared
// boundary point (edge midpoint orthogonally, corner vertex diagonally — at
// (dx·0.5, dz·0.5) either way), the height rise `dY` from `heights[i]`, and
// the 'YZX' pitch/yaw that lays the arm exactly between its two anchors.
function armFrame(i: number, heights: readonly number[]): {
  dx: number; dz: number; horiz: number; dY: number;
  pitch: number; yaw: number; len3D: number;
} {
  const [dx, dz] = DIRS8[i]!;
  const horiz = (i & 1) === 1 ? Math.SQRT1_2 : 0.5;
  const dY = heights[i] ?? 0;
  return {
    dx, dz, horiz, dY,
    pitch: Math.atan2(dY, horiz),
    yaw: Math.atan2(-dz, dx),
    len3D: Math.sqrt(horiz * horiz + dY * dY),
  };
}

// The junction post: dark plinth, dressed shaft, pale cap — plus a single
// merlon block once the wall is finished, so even a lone post carries the
// crenellated top line the camera reads.
function post(h: number, s: number): Part[] {
  const shaftH = h + 0.02;
  const parts: Part[] = [
    box(0.30, 0.06, 0.30, STONE_DK, [0, 0.03, 0]),
    box(0.22, shaftH, 0.22, STONE, [0, shaftH / 2, 0]),
    box(0.27, 0.05, 0.27, PLASTER, [0, shaftH + 0.025, 0]),
  ];
  if (s >= 3) parts.push(box(0.12, 0.08, 0.12, PLASTER, [0, shaftH + 0.05 + 0.04, 0]));
  return parts;
}

// Half-arms reaching out to every neighbouring wall tile: a stone body under
// a pale projecting cap, studded with merlons at stage 3 so the finished
// rampart's top line reads crenellated from above. A diagonal segment is
// skipped when the wall already joins through a flanking orthogonal.
function connectionArms(
  h: number, s: number, connections: number, heights: readonly number[],
): Part[] {
  const has = (i: number): boolean => (connections & (1 << i)) !== 0;
  const parts: Part[] = [];
  const bodyH = Math.max(0.06, h - CAP_H);
  for (let i = 0; i < 8; i++) {
    if (!has(i)) continue;
    const diagonal = (i & 1) === 1;
    if (diagonal && (has((i + 7) % 8) || has((i + 1) % 8))) continue;
    const f = armFrame(i, heights);
    const rot: [number, number, number] = [0, f.yaw, f.pitch];
    // Stone body — the half-arm's midpoint, halfway up the rise.
    parts.push({
      geometry: new THREE.BoxGeometry(f.len3D, bodyH, THICK),
      color: STONE,
      position: [f.dx * 0.25, bodyH / 2 + f.dY * 0.5, f.dz * 0.25],
      rotation: rot, eulerOrder: "YZX",
    });
    // Pale cap course riding the body top — the line the camera sees.
    parts.push({
      geometry: new THREE.BoxGeometry(f.len3D + 0.02, CAP_H, THICK + 0.06),
      color: PLASTER,
      position: [f.dx * 0.25, bodyH + CAP_H / 2 + f.dY * 0.5, f.dz * 0.25],
      rotation: rot, eulerOrder: "YZX",
    });
    // Crenellation: merlon blocks stud the finished cap, following the slope.
    if (s >= 3) {
      for (const t of [0.35, 0.72]) {
        parts.push({
          geometry: new THREE.BoxGeometry(0.10, 0.08, THICK + 0.02),
          color: PLASTER,
          position: [f.dx * 0.5 * t, f.dY * t + h + 0.04, f.dz * 0.5 * t],
          rotation: rot, eulerOrder: "YZX",
        });
      }
    }
  }
  return parts;
}

// Apply an additional world-Y rotation to a part list: rotates each position
// around the origin and adds `yaw` to the part's rotation.y. Valid when all
// parts have rotation.x = 0 — true for every gate part below.
function rotatePartsY(parts: Part[], yaw: number): Part[] {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return parts.map(p => {
    const pos = p.position;
    const rot = p.rotation;
    return {
      ...p,
      position: pos
        ? [pos[0] * c - pos[2] * s, pos[1], pos[0] * s + pos[2] * c] as [number, number, number]
        : pos,
      rotation: rot
        ? [rot[0], rot[1] + yaw, rot[2]] as [number, number, number]
        : [0, yaw, 0] as [number, number, number],
    };
  });
}

// ─── Banner arch gate (canonical: passage runs along ±Z) ─────────────────────
//
// Two dressed-stone pillars with pale crenellated caps carry a lintel and a
// pale coping; a diamond keystone sits under the lintel and a bold red banner
// hangs on the face above the passage. Two timber door wings swing open with
// `open` (0 = shut, 1 = fully open). Every part keeps rotation.x = 0 so
// rotatePartsY composes correctly (the keystone uses only a Z rotation).
function canonicalGate(open: number): Part[] {
  const PIL_X  = 0.40;    // pillar centres
  const PIL_W  = 0.22;    // pillar width along the gate face (X)
  const PIL_D  = 0.26;    // pillar depth along the passage (Z)
  const PIL_H  = 0.82;
  const OPEN_W = 2 * (PIL_X - PIL_W / 2);   // clear passage width = 0.58
  const DOOR_H = 0.55;    // top of the door opening (= lintel underside)

  const parts: Part[] = [];

  // Flanking pillars: shaft, pale cap, merlon — the wall's crenellated top
  // line carried up onto the gate towers.
  for (const sx of [-1, 1] as const) {
    parts.push(box(PIL_W, PIL_H, PIL_D, STONE, [sx * PIL_X, PIL_H / 2, 0]));
    parts.push(box(PIL_W + 0.06, 0.05, PIL_D + 0.06, PLASTER, [sx * PIL_X, PIL_H + 0.025, 0]));
    parts.push(box(0.12, 0.09, 0.14, PLASTER, [sx * PIL_X, PIL_H + 0.05 + 0.045, 0]));
  }

  // Lintel beam over the doorway, with a pale projecting coping above it.
  parts.push(box(0.62, 0.12, PIL_D - 0.02, STONE, [0, DOOR_H + 0.06, 0]));
  parts.push(box(0.70, 0.05, PIL_D + 0.04, PLASTER, [0, DOOR_H + 0.12 + 0.025, 0]));
  // Diamond keystone under the lintel centre — the "arch" read of the portal.
  parts.push(box(0.10, 0.10, PIL_D + 0.02, PLASTER, [0, DOOR_H + 0.01, 0], [0, 0, Math.PI / 4]));
  // Bold red banner hanging on the gate face above the passage, with a pale
  // emblem block — the one oversized iconic feature, readable from above.
  parts.push(box(0.30, 0.04, 0.04, TIMBER, [0, DOOR_H + 0.12, PIL_D / 2 + 0.04]));
  parts.push(box(0.26, 0.30, 0.025, ROOF_TRADE, [0, DOOR_H - 0.05, PIL_D / 2 + 0.045]));
  parts.push(box(0.10, 0.10, 0.03, PLASTER, [0, DOOR_H - 0.02, PIL_D / 2 + 0.05]));
  // Threshold sill across the passage floor.
  parts.push(box(OPEN_W, 0.03, 0.30, STONE_DK, [0, 0.015, 0]));

  // Two timber door wings, hinged on the pillar inner faces. Each wing pivots
  // around its hinge by `theta`: the wing centre is computed in the door's
  // local frame and swung around the hinge, exactly how buildModel composes
  // the rotation. BOTH wings swing to the SAME side of the passage (+Z): the
  // hinge stays fixed on its pillar, so `cz` does not depend on `sign` (the
  // sign only chooses which pillar each wing hangs from and its rotation).
  const DOOR_W = OPEN_W / 2;          // each wing covers half the opening
  const ARM    = DOOR_W / 2;          // hinge → wing-centre distance
  const theta  = open * Math.PI / 2;  // 0 = shut, π/2 = fully open
  for (const [hingeX, sign] of [[-OPEN_W / 2, 1], [OPEN_W / 2, -1]] as const) {
    const cx   = hingeX + sign * ARM * Math.cos(theta);
    const cz   = ARM * Math.sin(theta);   // same side for both wings
    const rotY = -sign * theta;
    parts.push({
      geometry: new THREE.BoxGeometry(DOOR_W - 0.01, DOOR_H, 0.045),
      color: TIMBER, position: [cx, DOOR_H / 2, cz], rotation: [0, rotY, 0],
    });
    parts.push({
      geometry: new THREE.BoxGeometry(DOOR_W * 0.84, 0.03, 0.06),
      color: IRON, position: [cx, DOOR_H * 0.55, cz], rotation: [0, rotY, 0],
    });
  }
  return parts;
}

// A wall the owner converted into a gate: the banner arch, oriented so the
// passage runs perpendicular to the wall run (classic orientation rule).
function gateParts(connections: number, open: number): Part[] {
  //   bit 0 = E (1), bit 2 = N (4), bit 4 = W (16), bit 6 = S (64)
  // An E-W wall (connects east/west) → passage is N-S → canonical yaw = 0.
  // An N-S wall (connects north/south) → passage is E-W → yaw = π/2.
  const hasE = (connections & 1) !== 0;
  const hasW = (connections & 16) !== 0;
  const hasN = (connections & 4) !== 0;
  const hasS = (connections & 64) !== 0;
  const wallNS = (hasN || hasS) && !(hasE || hasW);
  const yaw = wallNS ? Math.PI / 2 : 0;
  const arch = canonicalGate(open);
  return yaw ? rotatePartsY(arch, yaw) : arch;
}

// A dressed-stone wall with a pale crenellated top line. The junction post is
// always drawn; a capped segment reaches out to every neighbouring wall tile.
// A wall converted into a gate renders as a banner arch with timber doors.
function partsFor(
  s: number, connections = 0, gate = false, gateOpen = 0, heights: readonly number[] = [],
): Part[] {
  if (gate) return gateParts(connections, gateOpen);
  const h = STAGE_H[Math.max(0, Math.min(3, s))]!;
  return [
    ...post(h, s),
    ...connectionArms(h, s, connections, heights),
  ];
}

export const wallV3 = defineModel({
  id: "wall-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Wall (v3)",
  tags: ["building","wall","low-poly","age-of-mcp","v3","military"],
  summary: "Low-poly wall.",
  description: "A stone rampart segment, placed in runs to form a defensive wall.",
  author: "age-of-mcp",
  footprint: 0.32,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
